/**
 * src/hooks/useActionCount.js
 *
 * The ◎ badge count. Since ACEA-01 it is the length of the one shared list
 * (src/selectors/myActionItems.js) that the Action Centre renders, so the
 * badge and the list cannot disagree: actionCount === items.length.
 *
 * When a summed read failed (GH #8721) the count is a lower bound:
 * `actionBadge` is "n+", or "—" when nothing was read, never a bare n.
 *
 * The per-source fields below are kept for existing readers; each is a count
 * of that source's items in the same list.
 */

import { useMemo } from 'react';
import { useMyActionItems } from './useMyActionItems.js';

// Re-exported from their new home so every existing import path keeps working.
export {
  classifyRiskForUser,
  listVendorsNotResponding,
  listVendorsPendingArchiveProposal,
} from '../selectors/myActionItems.js';

/**
 * The badge text, or null when there is no badge. `incomplete` means a summed
 * read failed, so the true count is at least `count`.
 */
export function formatActionBadge(count, incomplete, cap = Infinity) {
  if (!incomplete && count === 0) return null;
  const shown = count > cap ? `${cap}+` : String(count);
  if (!incomplete) return shown;
  return count === 0 ? '—' : shown.endsWith('+') ? shown : `${shown}+`;
}

export const ACTION_BADGE_INCOMPLETE_TITLE =
  'Some of your work could not be read, so this count may be low.';

const MR_TASK_KINDS = new Set(['management-review-action', 'management-review-verification']);

/**
 * @param {object} state
 * @param {object} session
 * @param {Array}  users
 * @param {object} [extras] { obligationTasks, tenant } — the same inputs the
 *                          Action Centre is given; every badge caller passes them
 */
export function useActionCount(state, session, users = [], extras = {}) {
  const { items, incomplete } = useMyActionItems(state, session, users, extras);

  return useMemo(() => {
    const n = {};
    let openMRActions = 0;
    for (const item of items) {
      n[item.kind] = (n[item.kind] || 0) + 1;
      if (MR_TASK_KINDS.has(item.kind)) openMRActions++;
    }
    const c = (kind) => n[kind] || 0;
    const portalAnswersWaiting = c('portal-answer-waiting');
    const portalAnswersSentBack = c('portal-answer-sent-back');
    return {
      actionCount: items.length,
      actionCountIncomplete: incomplete,
      controlOwnerAlerts: c('control-owner-alert'),
      portalAnswersWaiting,
      portalAnswersSentBack,
      controlOwnerAnswers: portalAnswersWaiting + portalAnswersSentBack,
      hasDocumentApprovals: c('doc-approval') > 0,
      hasDocumentPublishes: c('doc-publish') > 0,
      pendingDocPublishes: c('doc-publish'),
      hasDocumentReviews: c('doc-review') > 0,
      pendingDocReads: c('doc-read'),
      pendingBIAInputs: c('bia-input'),
      pendingBIAReviews: c('bia-review'),
      biaReviewsDue: c('bia-review-due'),
      openMRActions,
      pendingMakeReady: c('risk-make-ready'),
      pendingReassessments: c('risk-reassessment'),
      pendingUARReviews: c('uar-campaign'),
      pendingSOAApprovals: c('soa-approval'),
      employeeDocAckGaps: c('employee-doc-ack-gap'),
      vendorReassessmentOverdue: c('vendor-not-responding'),
      vendorArchiveProposals: c('vendor-archive-proposal'),
      pendingRiskProposals: c('risk-proposal'),
      pendingIncidentProposals: c('incident-proposal'),
      pendingIncidentReclassifications: c('incident-reclassification'),
      closedIncidentsMissedNotification: c('incident-missed-notification'),
      pendingFindingProposals: c('finding-proposal'),
      gapQuestionAssignments: c('gap-questions'),
      aiGovernanceCount: c('ai-governance'),
      bugUpdates: c('bug-update'),
    };
  }, [items, incomplete]);
}
